/**
 * Mobile Drawer Header Component
 *
 * Contains navigation controls, search toggle, breadcrumbs,
 * and header actions for the mobile drawer.
 */

import React from "react";
import { Button } from "../../button";
import { ChevronRight, ChevronLeft, ArrowLeft, Search, X } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@west-tennessee-consulting/ui";
import { BreadcrumbItem } from "../mobile-drawer-types";

interface MobileDrawerHeaderProps {
  isSearchMode: boolean;
  canSearch?: boolean;
  navigationStack: BreadcrumbItem[];
  currentLevel: BreadcrumbItem;
  searchQuery: string;
  searchResults: any[];
  onSearchToggle: () => void;
  onNavigateBack: () => void;
  onNavigateToRoot: () => void;
  onNavigateToLevel: (levelIndex: number) => void;
  onClearSearch: () => void;
  onClose: () => void;
}

export function MobileDrawerHeader({
  isSearchMode,
  canSearch = true,
  navigationStack,
  currentLevel,
  searchQuery,
  searchResults,
  onSearchToggle,
  onNavigateBack,
  onNavigateToRoot,
  onNavigateToLevel,
  onClearSearch,
  onClose,
}: MobileDrawerHeaderProps) {
  return (
    <>
      {/* Navigation Header */}
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center space-x-2">
          {isSearchMode ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="sm" onClick={onSearchToggle} className="p-1" aria-label="Exit search">
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Exit search</TooltipContent>
            </Tooltip>
          ) : navigationStack.length > 1 ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="sm" onClick={onNavigateBack} className="p-1" aria-label="Back">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Back</TooltipContent>
            </Tooltip>
          ) : null}
          <h2 className="text-lg font-semibold text-card-foreground">
            {isSearchMode ? "Search" : currentLevel.name}
          </h2>
        </div>

        <div className="flex items-center space-x-2">
          {!isSearchMode && canSearch && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="sm" onClick={onSearchToggle} className="p-1" aria-label="Open search">
                  <Search className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Open search</TooltipContent>
            </Tooltip>
          )}
          {isSearchMode && canSearch && (searchQuery || searchResults.length > 0) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearSearch}
              className="p-1"
              title="Clear search"
            >
              <X className="h-3 w-3" />
            </Button>
          )}
          {!isSearchMode && navigationStack.length > 1 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onNavigateToRoot}
              className="text-xs px-2 py-1"
            >
              Menu
            </Button>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="sm" onClick={onClose} className="p-1" aria-label="Close menu">
                <X className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Close menu</TooltipContent>
          </Tooltip>
        </div>
      </div>

      {/* Breadcrumb trail for deeper navigation */}
      {!isSearchMode && navigationStack.length > 2 && (
        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
            {navigationStack.slice(0, -1).map((level, index) => (
              <React.Fragment key={level.id}>
                <button type="button"
                  onClick={() => onNavigateToLevel(index)}
                  className="hover:text-card-foreground transition-colors"
                >
                  {level.name}
                </button>
                <ChevronRight className="h-3 w-3" />
              </React.Fragment>
            ))}
            <span className="text-card-foreground font-medium">{currentLevel.name}</span>
          </div>
        </div>
      )}
    </>
  );
}
